/**
 * AiCallRecorder
 *
 * Browser-side capture of the spoken audio of a consultation call, used to build the
 * doctor-verified AI summary. Runs ONLY in the doctor client so there is a single transcript
 * per appointment. It is completely additive/isolated — it never touches the Agora call flow;
 * if anything fails it silently no-ops so the call is never affected.
 *
 * Capture strategy:
 *  - DOCTOR audio: captured from a dedicated getUserMedia() mic stream (reliable, always live,
 *    independent of Agora's internal track lifecycle). Falls back to the Agora local track.
 *  - PATIENT audio: the remote Agora track (from mediaStore).
 *  - Rolling chunks DURING the call: every `chunkMs`, a short clip of each speaker is recorded
 *    separately and uploaded for incremental Whisper transcription (separate files => perfect
 *    speaker labels, no diarization model).
 *  - One continuous MIXED recording (doctor + patient via Web Audio) for the review-screen
 *    playback, uploaded once on finalize().
 */

export interface AiCallRecorderConfig {
  apiBaseUrl: string
  appointmentId: number
  authToken: string
  /** Returns the doctor's live MediaStreamTrack (Agora local), or null. Used as a fallback. */
  getDoctorTrack: () => MediaStreamTrack | null
  /** Returns the patient's live MediaStreamTrack (Agora remote), or null. */
  getPatientTrack: () => MediaStreamTrack | null
  /** Rolling chunk length in ms (default 20s). */
  chunkMs?: number
  /**
   * How often (ms) the accumulated mixed audio is uploaded as a resilience checkpoint during
   * the call, so a partial recording survives even if the end-of-call finalize never runs
   * (default 30s). Values below 10s are clamped up to 30s.
   */
  mixedCheckpointMs?: number
}

const pickMimeType = (): string => {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus'
  ]
  for (const c of candidates) {
    try {
      if (
        typeof MediaRecorder !== 'undefined' &&
        MediaRecorder.isTypeSupported(c)
      ) {
        return c
      }
    } catch {
      /* ignore */
    }
  }
  return 'audio/webm'
}

const formatTime = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000))
  const mm = Math.floor(total / 60)
  const ss = total % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

export class AiCallRecorder {
  private cfg: AiCallRecorderConfig
  private mimeType: string
  private startedAt = 0
  private chunkTimer: number | undefined
  private reattachTimer: number | undefined
  private mixedCheckpointTimer: number | undefined
  private checkpointUploading = false
  private running = false
  private finalized = false

  // Dedicated doctor mic (getUserMedia) — reliable, independent of Agora.
  private doctorMicStream: MediaStream | null = null

  // Mixed (playback) recording.
  private audioCtx: AudioContext | null = null
  private mixedDest: MediaStreamAudioDestinationNode | null = null
  private mixedRecorder: MediaRecorder | null = null
  private mixedChunks: Blob[] = []
  private mixedSources = new Set<MediaStreamTrack>()

  constructor(cfg: AiCallRecorderConfig) {
    this.cfg = cfg
    this.mimeType = pickMimeType()
  }

  /** Begin capture. Safe to call multiple times (no-op after the first). */
  start(): void {
    if (this.running || this.finalized) return
    if (!this.cfg?.appointmentId || !this.cfg?.authToken) {
      console.warn(
        '[AI recorder] missing appointmentId/authToken — not starting'
      )
      return
    }
    this.running = true
    this.startedAt = Date.now()
    console.log('[AI recorder] starting (mime:', this.mimeType, ')')
    // Async init so getUserMedia can resolve without blocking.
    this.init().catch((e) => console.warn('[AI recorder] init error:', e))
  }

  private async init(): Promise<void> {
    // 1) Grab a dedicated doctor mic stream — this is the reliable source for the doctor voice.
    try {
      if (navigator?.mediaDevices?.getUserMedia) {
        this.doctorMicStream = await navigator.mediaDevices.getUserMedia({
          audio: true
        })
        console.log('[AI recorder] doctor mic captured via getUserMedia')
      }
    } catch (e) {
      console.warn(
        '[AI recorder] getUserMedia failed, falling back to Agora track:',
        e
      )
    }

    if (!this.running) return

    // 2) Start the continuous mixed recording (best-effort).
    try {
      this.startMixedRecording()
    } catch (e) {
      console.warn('[AI recorder] mixed recording unavailable:', e)
    }

    // 2b) Periodically upload the accumulated mixed audio as a resilience checkpoint, so a
    // partial recording survives even if the end-of-call finalize never runs.
    const checkpointMs =
      this.cfg.mixedCheckpointMs && this.cfg.mixedCheckpointMs >= 10000
        ? this.cfg.mixedCheckpointMs
        : 30000
    this.mixedCheckpointTimer = window.setInterval(() => {
      this.uploadMixedCheckpoint().catch(() => undefined)
    }, checkpointMs)

    // 3) Rolling chunk loop.
    const chunkMs =
      this.cfg.chunkMs && this.cfg.chunkMs > 3000 ? this.cfg.chunkMs : 20000
    const loop = () => {
      if (!this.running) return
      this.recordAndUploadChunk('doctor').catch(() => undefined)
      this.recordAndUploadChunk('patient').catch(() => undefined)
    }
    this.chunkTimer = window.setInterval(loop, chunkMs)
    // First chunk shortly after start so the transcript begins building quickly.
    window.setTimeout(loop, Math.min(chunkMs, 6000))
  }

  /**
   * Stop capture, upload the final chunk(s) + the mixed playback audio, and trigger the
   * server-side summary generation. Bounded so it can never hang the end-call flow.
   */
  async finalize(): Promise<void> {
    if (this.finalized) return
    this.finalized = true
    this.running = false
    if (this.chunkTimer) {
      window.clearInterval(this.chunkTimer)
      this.chunkTimer = undefined
    }
    if (this.mixedCheckpointTimer) {
      window.clearInterval(this.mixedCheckpointTimer)
      this.mixedCheckpointTimer = undefined
    }
    if (this.reattachTimer) {
      window.clearInterval(this.reattachTimer)
      this.reattachTimer = undefined
    }

    // Capture one last short clip of each speaker so the final words are transcribed.
    // (Tracks are still live here because we hold our own doctor mic; the patient track may
    // already be gone, which is fine.)
    try {
      await Promise.all([
        this.recordAndUploadChunk('doctor', 4000),
        this.recordAndUploadChunk('patient', 4000)
      ])
    } catch {
      /* ignore */
    }

    // Stop the mixed recorder and collect the playback blob.
    const mixedBlob = await this.stopMixedRecording().catch(() => null)
    console.log(
      '[AI recorder] finalize — mixed blob size:',
      mixedBlob ? mixedBlob.size : 0
    )

    // Upload mixed audio + trigger summary generation.
    try {
      const form = new FormData()
      if (mixedBlob && mixedBlob.size > 0) {
        form.append(
          'file',
          mixedBlob,
          `appointment_${this.cfg.appointmentId}_mixed.webm`
        )
      }
      const res = await fetch(
        `${this.cfg.apiBaseUrl}/consultation/appointments/${this.cfg.appointmentId}/ai-summary/finalize`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.cfg.authToken}` },
          body: form
        }
      )
      console.log('[AI recorder] finalize response status:', res.status)
    } catch (e) {
      console.warn('[AI recorder] finalize upload failed:', e)
    }

    // Release the dedicated doctor mic.
    try {
      this.doctorMicStream?.getTracks().forEach((t) => t.stop())
    } catch {
      /* ignore */
    }
    this.doctorMicStream = null
  }

  /**
   * Upload the accumulated mixed audio so far as a checkpoint. The first MediaRecorder chunk
   * carries the WebM init segment, so concatenating all chunks yields a complete, playable
   * file of the call up to this point. This overwrites the previous partial recording
   * server-side (stable key), so the latest checkpoint is always available even if finalize
   * never runs. Best-effort and non-overlapping; never throws into the call flow.
   */
  private async uploadMixedCheckpoint(): Promise<void> {
    if (!this.running || this.finalized) return
    if (this.checkpointUploading) return
    if (!this.mixedChunks.length) return

    let blob: Blob
    try {
      blob = new Blob([...this.mixedChunks], { type: this.mimeType })
    } catch {
      return
    }
    if (!blob.size) return

    this.checkpointUploading = true
    try {
      const form = new FormData()
      form.append(
        'file',
        blob,
        `appointment_${this.cfg.appointmentId}_mixed.webm`
      )
      const res = await fetch(
        `${this.cfg.apiBaseUrl}/consultation/appointments/${this.cfg.appointmentId}/ai-summary/audio`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.cfg.authToken}` },
          body: form
        }
      )
      console.log(
        '[AI recorder] mixed audio checkpoint uploaded — size:',
        blob.size,
        'status:',
        res.status
      )
    } catch (e) {
      console.warn('[AI recorder] checkpoint upload failed:', e)
    } finally {
      this.checkpointUploading = false
    }
  }

  // ---------- internals ----------

  /** Resolve the doctor track: prefer our own getUserMedia mic, fall back to the Agora track. */
  private doctorTrack(): MediaStreamTrack | null {
    const own = this.doctorMicStream?.getAudioTracks?.()[0] || null
    if (own && own.readyState === 'live') return own
    return this.cfg.getDoctorTrack()
  }

  private patientTrack(): MediaStreamTrack | null {
    return this.cfg.getPatientTrack()
  }

  private startMixedRecording(): void {
    const AC: typeof AudioContext =
      (window as any).AudioContext || (window as any).webkitAudioContext
    if (!AC) return
    this.audioCtx = new AC()
    this.mixedDest = this.audioCtx.createMediaStreamDestination()

    const attach = (track: MediaStreamTrack | null) => {
      if (
        !track ||
        this.mixedSources.has(track) ||
        !this.audioCtx ||
        !this.mixedDest
      ) {
        return
      }
      try {
        const src = this.audioCtx.createMediaStreamSource(
          new MediaStream([track])
        )
        src.connect(this.mixedDest)
        this.mixedSources.add(track)
        console.log('[AI recorder] attached a track to the mixed recording')
      } catch {
        /* ignore */
      }
    }
    attach(this.doctorTrack())
    attach(this.patientTrack())

    // Re-attach tracks that appear later (e.g. patient joins/reconnects, or the doctor
    // mic resolves after start).
    this.reattachTimer = window.setInterval(() => {
      if (!this.running) {
        if (this.reattachTimer) window.clearInterval(this.reattachTimer)
        return
      }
      attach(this.doctorTrack())
      attach(this.patientTrack())
    }, 4000)

    this.mixedRecorder = new MediaRecorder(this.mixedDest.stream, {
      mimeType: this.mimeType
    })
    this.mixedRecorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) this.mixedChunks.push(ev.data)
    }
    this.mixedRecorder.start(1000)
  }

  private stopMixedRecording(): Promise<Blob | null> {
    const buildBlob = () =>
      this.mixedChunks.length
        ? new Blob(this.mixedChunks, { type: this.mimeType })
        : null
    return new Promise((resolve) => {
      const rec = this.mixedRecorder
      if (!rec || rec.state === 'inactive') {
        resolve(buildBlob())
        return
      }
      rec.onstop = () => {
        try {
          this.audioCtx?.close()
        } catch {
          /* ignore */
        }
        resolve(buildBlob())
      }
      try {
        rec.stop()
      } catch {
        resolve(buildBlob())
      }
    })
  }

  /** Record a short clip of one speaker and upload it for incremental transcription. */
  private recordAndUploadChunk(
    speaker: 'doctor' | 'patient',
    durationMs = 12000
  ): Promise<void> {
    return new Promise((resolve) => {
      const track =
        speaker === 'doctor' ? this.doctorTrack() : this.patientTrack()
      if (!track || track.readyState !== 'live') {
        resolve()
        return
      }
      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(new MediaStream([track]), {
          mimeType: this.mimeType
        })
      } catch {
        resolve()
        return
      }
      const chunks: Blob[] = []
      const timeLabel = formatTime(Date.now() - this.startedAt)
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data)
      }
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: this.mimeType })
        if (blob.size > 0) {
          this.uploadChunk(blob, speaker, timeLabel).finally(() => resolve())
        } else {
          resolve()
        }
      }
      try {
        recorder.start()
      } catch {
        resolve()
        return
      }
      window.setTimeout(() => {
        try {
          if (recorder.state !== 'inactive') recorder.stop()
        } catch {
          resolve()
        }
      }, durationMs)
    })
  }

  private async uploadChunk(
    blob: Blob,
    speaker: 'doctor' | 'patient',
    timeLabel: string
  ): Promise<void> {
    try {
      const form = new FormData()
      form.append('file', blob, `${speaker}_${Date.now()}.webm`)
      form.append('speaker', speaker)
      form.append('time', timeLabel)
      form.append('ts', String(Date.now()))
      const res = await fetch(
        `${this.cfg.apiBaseUrl}/consultation/appointments/${this.cfg.appointmentId}/ai-summary/transcript-chunk`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.cfg.authToken}` },
          body: form
        }
      )
      console.log(
        `[AI recorder] ${speaker} chunk uploaded (${blob.size} bytes) — status ${res.status}`
      )
    } catch (e) {
      // Losing one chunk is acceptable per the plan.
      console.warn('[AI recorder] chunk upload failed:', e)
    }
  }
}
